// PDFDocument.js - Complete PDF Export Component
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font
} from '@react-pdf/renderer';

// Register fonts (optional - you can remove this if fonts don't load)
try {
  Font.register({
    family: 'Roboto',
    fonts: [
      {
        src: 'https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmSU5fBBc4.ttf',
        fontWeight: 400,
      },
      {
        src: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.ttf',
        fontWeight: 700,
      },
    ],
  });
} catch (error) {
  console.log('Font loading failed, using default fonts');
}

// Define comprehensive styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 20,
   paddingBottom: 50, // Add more bottom padding for page numbers
    fontFamily: 'Helvetica',
    fontSize: 10,
  },
  
  // Header styles
  header: {
    backgroundColor: '#0f274b',
    color: 'white',
    padding: 15,
    marginBottom: 20,
    borderRadius: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flexDirection: 'column',
  },
  headerRight: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  headerSubtitle: {
    fontSize: 12,
    fontWeight: 'normal',
  },
  headerInfo: {
    fontSize: 10,
    fontWeight: 'normal',
  },
  
  // Info box styles
  infoBox: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 4,
    marginBottom: 15,
    borderLeft: 3,
    borderLeftColor: '#c29946',
  },
  infoTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#0f274b',
  },
  infoText: {
    fontSize: 9,
    color: '#666666',
    lineHeight: 1.4,
    marginBottom: 2,
  },
  
  // Section styles
  section: {
    marginBottom: 20,
    pageBreakInside: false,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0f274b',
    marginBottom: 15,
    borderBottom: 1,
    borderBottomColor: '#c29946',
    paddingBottom: 5,
  },
  
  // Metrics grid styles
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  metricCard: {
    width: '48%',
    backgroundColor: '#f8f9fa',
    padding: 12,
    marginBottom: 8,
    marginRight: '2%',
    borderRadius: 4,
    borderLeft: 3,
    borderLeftColor: '#c29946',
  },
  metricLabel: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 3,
  },
  metricValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0f274b',
  },
  
  // Table styles
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRightWidth: 0,
        maxHeight: '90%', // Prevent tables from getting too tall
    borderBottomWidth: 0,
        marginBottom: 30, // More space between tables and page numbers

  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
    minHeight: 28
  },
  tableHeaderRow: {
    backgroundColor: '#0f274b',
    color: 'white',
    fontWeight: 'bold',
  },
  tableAlternateRow: {
    backgroundColor: '#f8f9fa',
  },
  tableTotalRow: {
    backgroundColor: '#c29946',
    color: 'white',
    fontWeight: 'bold',
  },
  tableCol: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 8,
    justifyContent: 'center',
    fontSize: 8,
  },
  tableColHeader: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 10,
    justifyContent: 'center',
    fontSize: 9,
    fontWeight: 'bold',
  },
  tableCell: {
    fontSize: 8,
    textAlign: 'left',
    flexWrap: 'wrap',
  },
  tableCellCenter: {
    fontSize: 8,
    textAlign: 'center',
  },
  tableCellRight: {
    fontSize: 8,
    textAlign: 'right',
  },
  tableCellHeader: {
    fontSize: 9,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    flexWrap: 'wrap',
  },
  tableCellTotal: {
    fontSize: 9,
    fontWeight: 'bold',
    color: 'white',
    flexWrap: 'wrap',
  },
  
  // Footer styles
  pageNumber: {
    position: 'absolute',
    fontSize: 8,
    bottom: 20, // Move higher to avoid overlap
    left: 0,
 backgroundColor: '#ffffff', // Add white background to prevent overlap

    right: 0,
    textAlign: 'center',
    color: '#666666',
  },
  
  // Special row styles
  orderHeaderRow: {
    backgroundColor: '#e3f2fd',
    fontWeight: 'bold',
    minHeight: 32, // Taller for better visibility
  },
  propertyGroupRow: {
    backgroundColor: '#f3e5f5',
    fontWeight: 'bold',
  },
  grandTotalRow: {
    backgroundColor: '#c29946',
    color: 'white',
    fontWeight: 'bold',
  },
});

// Overview Report Component
const OverviewReport = ({ metrics, showPricesWithoutVAT }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>Sales Overview</Text>
    <View style={styles.metricsGrid}>
      <View style={styles.metricCard}>
        <Text style={styles.metricLabel}>Total Orders</Text>
        <Text style={styles.metricValue}>{metrics.totalOrders || 0}</Text>
      </View>
      <View style={styles.metricCard}>
        <Text style={styles.metricLabel}>Successful Orders</Text>
        <Text style={styles.metricValue}>
          {metrics.successfulOrders || 0} ({((metrics.successfulOrders || 0)/(Math.max(metrics.totalOrders, 1))*100).toFixed(1)}%)
        </Text>
      </View>
      <View style={styles.metricCard}>
        <Text style={styles.metricLabel}>Failed Orders</Text>
        <Text style={styles.metricValue}>
          {metrics.failedOrders || 0} ({((metrics.failedOrders || 0)/(Math.max(metrics.totalOrders, 1))*100).toFixed(1)}%)
        </Text>
      </View>
      <View style={styles.metricCard}>
        <Text style={styles.metricLabel}>Canceled Orders</Text>
        <Text style={styles.metricValue}>
          {metrics.canceledOrders || 0} ({((metrics.canceledOrders || 0)/(Math.max(metrics.totalOrders, 1))*100).toFixed(1)}%)
        </Text>
      </View>
      <View style={styles.metricCard}>
        <Text style={styles.metricLabel}>
          {showPricesWithoutVAT ? 'Revenue (excl. VAT)' : 'Total Revenue'}
        </Text>
        <Text style={styles.metricValue}>£{(metrics.totalRevenue || 0).toFixed(2)}</Text>
      </View>
      {showPricesWithoutVAT && (
        <>
          <View style={styles.metricCard}>
            <Text style={styles.metricLabel}>VAT Amount</Text>
            <Text style={styles.metricValue}>£{(metrics.totalVAT || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.metricCard}>
            <Text style={styles.metricLabel}>Total (incl. VAT)</Text>
            <Text style={styles.metricValue}>£{((metrics.totalRevenue || 0) + (metrics.totalVAT || 0)).toFixed(2)}</Text>
          </View>
        </>
      )}
      <View style={styles.metricCard}>
        <Text style={styles.metricLabel}>Average Order Value</Text>
        <Text style={styles.metricValue}>£{(metrics.averageOrderValue || 0).toFixed(2)}</Text>
      </View>
    </View>
  </View>
);

// Property Summary Table Component
const PropertySummaryTable = ({ data, showPricesWithoutVAT }) => {
  if (!data || data.length === 0) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Property Summary</Text>
        <Text>No property data available</Text>
      </View>
    );
  }

  const columnWidths = showPricesWithoutVAT 
    ? ['22%', '25%', '8%', '8%', '8%', '8%', '10%', '8%', '12%']
    : ['28%', '32%', '10%', '10%', '10%', '10%', '15%'];

  const headers = showPricesWithoutVAT
    ? ['Property', 'Address', 'Orders', 'Success', 'Failed', 'Canceled', 'Revenue (excl)', 'VAT', 'Total (incl)']
    : ['Property', 'Address', 'Orders', 'Success', 'Failed', 'Canceled', 'Revenue'];

  // Calculate totals
  const totals = data.reduce((acc, p) => ({
    orders: acc.orders + (p.totalOrders || 0),
    success: acc.success + (p.successfulOrders || 0),
    failed: acc.failed + (p.failedOrders || 0),
    canceled: acc.canceled + (p.canceledOrders || 0),
    revenue: acc.revenue + (p.totalRevenue || 0),
    vat: acc.vat + (p.totalVAT || 0)
  }), { orders: 0, success: 0, failed: 0, canceled: 0, revenue: 0, vat: 0 });

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Property Summary</Text>
      <View style={styles.table}>
        {/* Header */}
        <View style={[styles.tableRow, styles.tableHeaderRow]}>
          {headers.map((header, index) => (
            <View key={index} style={[styles.tableColHeader, { width: columnWidths[index] }]}>
              <Text style={styles.tableCellHeader}>{header}</Text>
            </View>
          ))}
        </View>

        {/* Data rows */}
        {data.map((property, index) => (
          <View key={index} style={[styles.tableRow, index % 2 === 1 ? styles.tableAlternateRow : {}]}>
            <View style={[styles.tableCol, { width: columnWidths[0] }]}>
              <Text style={styles.tableCell}>{property.propertyTitle || 'N/A'}</Text>
            </View>
            <View style={[styles.tableCol, { width: columnWidths[1] }]}>
              <Text style={styles.tableCell}>{property.propertyAddress || 'N/A'}</Text>
            </View>
            <View style={[styles.tableCol, { width: columnWidths[2] }]}>
              <Text style={styles.tableCellCenter}>{property.totalOrders || 0}</Text>
            </View>
            <View style={[styles.tableCol, { width: columnWidths[3] }]}>
              <Text style={styles.tableCellCenter}>{property.successfulOrders || 0}</Text>
            </View>
            <View style={[styles.tableCol, { width: columnWidths[4] }]}>
              <Text style={styles.tableCellCenter}>{property.failedOrders || 0}</Text>
            </View>
            <View style={[styles.tableCol, { width: columnWidths[5] }]}>
              <Text style={styles.tableCellCenter}>{property.canceledOrders || 0}</Text>
            </View>
            <View style={[styles.tableCol, { width: columnWidths[6] }]}>
              <Text style={styles.tableCellRight}>£{(property.totalRevenue || 0).toFixed(2)}</Text>
            </View>
            {showPricesWithoutVAT && (
              <>
                <View style={[styles.tableCol, { width: columnWidths[7] }]}>
                  <Text style={styles.tableCellRight}>£{(property.totalVAT || 0).toFixed(2)}</Text>
                </View>
                <View style={[styles.tableCol, { width: columnWidths[8] }]}>
                  <Text style={styles.tableCellRight}>£{((property.totalRevenue || 0) + (property.totalVAT || 0)).toFixed(2)}</Text>
                </View>
              </>
            )}
          </View>
        ))}

        {/* Total row */}
        <View style={[styles.tableRow, styles.tableTotalRow]}>
          <View style={[styles.tableCol, { width: columnWidths[0] }]}>
            <Text style={styles.tableCellTotal}>TOTAL</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[1] }]}>
            <Text style={styles.tableCellTotal}>{data.length} properties</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[2] }]}>
            <Text style={styles.tableCellTotal}>{totals.orders}</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[3] }]}>
            <Text style={styles.tableCellTotal}>{totals.success}</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[4] }]}>
            <Text style={styles.tableCellTotal}>{totals.failed}</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[5] }]}>
            <Text style={styles.tableCellTotal}>{totals.canceled}</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[6] }]}>
            <Text style={styles.tableCellTotal}>£{totals.revenue.toFixed(2)}</Text>
          </View>
          {showPricesWithoutVAT && (
            <>
              <View style={[styles.tableCol, { width: columnWidths[7] }]}>
                <Text style={styles.tableCellTotal}>£{totals.vat.toFixed(2)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[8] }]}>
                <Text style={styles.tableCellTotal}>£{(totals.revenue + totals.vat).toFixed(2)}</Text>
              </View>
            </>
          )}
        </View>
      </View>
    </View>
  );
};

// Order Details Table Component (Default View)
const OrderDetailsTable = ({ data, showPricesWithoutVAT }) => {
  if (!data || data.length === 0) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Order Details</Text>
        <Text>No order data available</Text>
      </View>
    );
  }

  const columnWidths = showPricesWithoutVAT
    ? ['8%', '12%', '18%', '20%', '10%', '12%', '10%', '10%']
    : ['10%', '15%', '22%', '25%', '12%', '16%'];

  const headers = showPricesWithoutVAT
    ? ['Order #', 'Date', 'Customer', 'Property', 'Status', 'Total (excl)', 'VAT', 'Total (incl)']
    : ['Order #', 'Date', 'Customer', 'Property', 'Status', 'Total'];

  // Calculate totals
  const totalOrderValue = data.reduce((sum, order) => sum + parseFloat(order.order_total || 0), 0);
  const totalVATValue = data.reduce((sum, order) => sum + parseFloat(order.vat_amount || 0), 0);

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Order Details</Text>
      <View style={styles.table}>
        {/* Header */}
        <View style={[styles.tableRow, styles.tableHeaderRow]}>
          {headers.map((header, index) => (
            <View key={index} style={[styles.tableColHeader, { width: columnWidths[index] }]}>
              <Text style={styles.tableCellHeader}>{header}</Text>
            </View>
          ))}
        </View>

        {/* Data rows */}
        {data.map((order, index) => {
          const orderTotal = parseFloat(order.order_total || 0);
          const orderVAT = parseFloat(order.vat_amount || 0);
          
          return (
            <View key={index} style={[styles.tableRow, index % 2 === 1 ? styles.tableAlternateRow : {}]}>
              <View style={[styles.tableCol, { width: columnWidths[0] }]}>
                <Text style={styles.tableCell}>{order.id || 'N/A'}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[1] }]}>
                <Text style={styles.tableCell}>
                  {order.property_date ? new Date(order.property_date).toLocaleDateString() : 'N/A'}
                </Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[2] }]}>
                <Text style={styles.tableCell}>
                  {`${order.delivery_first_name || ''} ${order.delivery_last_name || ''}`.trim() || 'N/A'}
                </Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[3] }]}>
                <Text style={styles.tableCell}>
                  {order.order_property?.property_title || 'N/A'}
                </Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[4] }]}>
                <Text style={styles.tableCellCenter}>{order.order_status || 'N/A'}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[5] }]}>
                <Text style={styles.tableCellRight}>
                  £{showPricesWithoutVAT ? (orderTotal - orderVAT).toFixed(2) : orderTotal.toFixed(2)}
                </Text>
              </View>
              {showPricesWithoutVAT && (
                <>
                  <View style={[styles.tableCol, { width: columnWidths[6] }]}>
                    <Text style={styles.tableCellRight}>£{orderVAT.toFixed(2)}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: columnWidths[7] }]}>
                    <Text style={styles.tableCellRight}>£{orderTotal.toFixed(2)}</Text>
                  </View>
                </>
              )}
            </View>
          );
        })}

        {/* Total row */}
        <View style={[styles.tableRow, styles.tableTotalRow]}>
          <View style={[styles.tableCol, { width: columnWidths[0] }]}>
            <Text style={styles.tableCellTotal}>TOTAL</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[1] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[2] }]}>
            <Text style={styles.tableCellTotal}>{data.length} orders</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[3] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[4] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[5] }]}>
            <Text style={styles.tableCellTotal}>
              £{showPricesWithoutVAT ? (totalOrderValue - totalVATValue).toFixed(2) : totalOrderValue.toFixed(2)}
            </Text>
          </View>
          {showPricesWithoutVAT && (
            <>
              <View style={[styles.tableCol, { width: columnWidths[6] }]}>
                <Text style={styles.tableCellTotal}>£{totalVATValue.toFixed(2)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[7] }]}>
                <Text style={styles.tableCellTotal}>£{totalOrderValue.toFixed(2)}</Text>
              </View>
            </>
          )}
        </View>
      </View>
    </View>
  );
};

// FIXED Order Items Detail Component - Replace the existing one in PDFDocument.js

const OrderItemsDetail = ({ data, showPricesWithoutVAT }) => {
  if (!data || data.length === 0) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Order Items Detail</Text>
        <Text>No order data available</Text>
      </View>
    );
  }

  // Adjust column widths to prevent overlap
  const columnWidths = showPricesWithoutVAT
    ? ['9%', '11%', '14%', '15%', '9%', '15%', '6%', '7%', '7%', '7%']
    : ['12%', '14%', '18%', '18%', '12%', '18%', '8%'];

  const headers = showPricesWithoutVAT
    ? ['Order ID', 'Date', 'Customer', 'Property', 'Status', 'Item Name', 'Qty', 'Price (excl)', 'VAT', 'Total']
    : ['Order ID', 'Date', 'Customer', 'Property', 'Status', 'Item Name', 'Qty', 'Price'];

  const allItems = [];
  let totalItemValue = 0;
  let totalItemVAT = 0;
  let totalOrderValue = 0;

  // Process orders and create flattened structure
  data.forEach(order => {
    const orderItems = order.order_products || [];
    const orderTotal = parseFloat(order.order_total || 0);
    const orderVAT = parseFloat(order.vat_amount || 0);
    
    totalOrderValue += orderTotal;

    // Add order header with complete information
    allItems.push({
      type: 'order_header',
      orderId: order.id,
      date: order.property_date ? new Date(order.property_date).toLocaleDateString() : '',
      customer: `${order.delivery_first_name || ''} ${order.delivery_last_name || ''}`.trim(),
      property: order.order_property?.property_title || '',
      status: order.order_status || '',
      itemName: `--- ORDER ${order.id} HEADER ---`,
      quantity: '',
      price: '',
      itemVAT: '',
      itemTotal: '',
      orderTotal: orderTotal,
      orderVAT: orderVAT,
      showOrderTotal: true
    });

    if (orderItems.length === 0) {
      allItems.push({
        type: 'no_items',
        orderId: '', // Don't repeat order ID
        date: '',
        customer: '',
        property: '',
        status: '',
        itemName: 'No item details available',
        quantity: '',
        price: '',
        itemVAT: '',
        itemTotal: ''
      });
    } else {
      orderItems.forEach((item, itemIndex) => {
        const price = parseFloat(item.order_products_price || 0);
        const qty = parseInt(item.order_products_qty || 0);
        const itemVAT = showPricesWithoutVAT ? parseFloat(item.vat_amount || 0) : 0;
        const itemPriceExcl = showPricesWithoutVAT ? price - itemVAT : price;
        
        totalItemValue += price * qty;
        totalItemVAT += itemVAT * qty;

        allItems.push({
          type: 'item',
          orderId: itemIndex === 0 ? '' : '', // Only show order ID on first item
          date: '',
          customer: '',
          property: '',
          status: '',
          itemName: item.order_products_name || 'Unknown Item',
          quantity: qty,
          price: price,
          itemPriceExcl: itemPriceExcl,
          itemVAT: itemVAT,
          itemTotal: price * qty
        });
      });
    }
  });

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Order Items Detail</Text>
      <View style={styles.table}>
        {/* Header */}
        <View style={[styles.tableRow, styles.tableHeaderRow]}>
          {headers.map((header, index) => (
            <View key={index} style={[styles.tableColHeader, { width: columnWidths[index] }]}>
              <Text style={styles.tableCellHeader}>{header}</Text>
            </View>
          ))}
        </View>

        {/* Data rows */}
        {allItems.map((item, index) => {
          const isOrderHeader = item.type === 'order_header';
          const isNoItems = item.type === 'no_items';
          const rowStyle = [
            styles.tableRow,
            isOrderHeader ? styles.orderHeaderRow : (index % 2 === 1 ? styles.tableAlternateRow : {})
          ];

          return (
            <View key={`item-${index}`} style={rowStyle}>
              <View style={[styles.tableCol, { width: columnWidths[0] }]}>
                <Text style={styles.tableCell}>{item.orderId || ''}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[1] }]}>
                <Text style={styles.tableCell}>{item.date || ''}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[2] }]}>
                <Text style={styles.tableCell}>{item.customer || ''}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[3] }]}>
                <Text style={styles.tableCell}>{item.property || ''}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[4] }]}>
                <Text style={styles.tableCellCenter}>{item.status || ''}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[5] }]}>
                <Text style={styles.tableCell}>{item.itemName || ''}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[6] }]}>
                <Text style={styles.tableCellCenter}>{item.quantity || ''}</Text>
              </View>
              
              {/* Price columns */}
              {showPricesWithoutVAT ? (
                <>
                  <View style={[styles.tableCol, { width: columnWidths[7] }]}>
                    <Text style={styles.tableCellRight}>
                      {item.itemPriceExcl ? `£${item.itemPriceExcl.toFixed(2)}` : 
                       (isOrderHeader && item.showOrderTotal) ? `£${(item.orderTotal - item.orderVAT).toFixed(2)}` : ''}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: columnWidths[8] }]}>
                    <Text style={styles.tableCellRight}>
                      {item.itemVAT ? `£${item.itemVAT.toFixed(2)}` : 
                       (isOrderHeader && item.showOrderTotal) ? `£${item.orderVAT.toFixed(2)}` : ''}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: columnWidths[9] }]}>
                    <Text style={styles.tableCellRight}>
                      {item.itemTotal ? `£${item.itemTotal.toFixed(2)}` : 
                       (isOrderHeader && item.showOrderTotal) ? `£${item.orderTotal.toFixed(2)}` : ''}
                    </Text>
                  </View>
                </>
              ) : (
                <View style={[styles.tableCol, { width: columnWidths[7] }]}>
                  <Text style={styles.tableCellRight}>
                    {item.price ? `£${item.price.toFixed(2)}` : 
                     (isOrderHeader && item.showOrderTotal) ? `£${item.orderTotal.toFixed(2)}` : ''}
                  </Text>
                </View>
              )}
            </View>
          );
        })}

        {/* Grand Total row */}
        <View style={[styles.tableRow, styles.grandTotalRow]}>
          <View style={[styles.tableCol, { width: columnWidths[0] }]}>
            <Text style={styles.tableCellTotal}>TOTAL</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[1] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[2] }]}>
            <Text style={styles.tableCellTotal}>{data.length} orders</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[3] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[4] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[5] }]}>
            <Text style={styles.tableCellTotal}>ALL ITEMS</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[6] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          
          {showPricesWithoutVAT ? (
            <>
              <View style={[styles.tableCol, { width: columnWidths[7] }]}>
                <Text style={styles.tableCellTotal}>£{(totalOrderValue - totalItemVAT).toFixed(2)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[8] }]}>
                <Text style={styles.tableCellTotal}>£{totalItemVAT.toFixed(2)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[9] }]}>
                <Text style={styles.tableCellTotal}>£{totalOrderValue.toFixed(2)}</Text>
              </View>
            </>
          ) : (
            <View style={[styles.tableCol, { width: columnWidths[7] }]}>
              <Text style={styles.tableCellTotal}>£{totalOrderValue.toFixed(2)}</Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

// Grouped Orders Component
const GroupedOrdersTable = ({ data, showPricesWithoutVAT, prepareOrderDetailsData }) => {
  if (!data || data.length === 0) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Orders Grouped by Property</Text>
        <Text>No order data available</Text>
      </View>
    );
  }

  const columnWidths = showPricesWithoutVAT
    ? ['25%', '10%', '12%', '18%', '10%', '10%', '8%', '7%']
    : ['30%', '12%', '15%', '20%', '12%', '11%'];

  const headers = showPricesWithoutVAT
    ? ['Property/Order', 'Order ID', 'Date', 'Customer', 'Status', 'Total (excl)', 'VAT', 'Total (incl)']
    : ['Property/Order', 'Order ID', 'Date', 'Customer', 'Status', 'Total'];

  // Get grouped data
  const groupedData = prepareOrderDetailsData(data, 'grouped');
  const dataSource = groupedData.dataSource || [];

  // Calculate grand totals
  const grandTotalRevenue = dataSource
    .filter(item => item.isPropertyGroup)
    .reduce((sum, item) => sum + (item.totalRevenue || 0), 0);
  const grandTotalVAT = dataSource
    .filter(item => item.isPropertyGroup)
    .reduce((sum, item) => sum + (item.totalVAT || 0), 0);
  const grandTotalOrders = dataSource
    .filter(item => item.isPropertyGroup)
    .reduce((sum, item) => sum + (item.ordersCount || 0), 0);
  const totalProperties = dataSource
    .filter(item => item.isPropertyGroup).length;

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Orders Grouped by Property</Text>
      <View style={styles.table}>
        {/* Header */}
        <View style={[styles.tableRow, styles.tableHeaderRow]}>
          {headers.map((header, index) => (
            <View key={index} style={[styles.tableColHeader, { width: columnWidths[index] }]}>
              <Text style={styles.tableCellHeader}>{header}</Text>
            </View>
          ))}
        </View>

        {/* Data rows */}
        {dataSource.map((item, index) => {
          const isPropertyGroup = item.isPropertyGroup;
          const rowStyle = isPropertyGroup ? styles.propertyGroupRow : (index % 2 === 1 ? styles.tableAlternateRow : {});

          return (
            <View key={index} style={[styles.tableRow, rowStyle]}>
              <View style={[styles.tableCol, { width: columnWidths[0] }]}>
                <Text style={styles.tableCell}>
                  {isPropertyGroup 
                    ? `${item.property} (${item.ordersCount} orders)`
                    : `  Order ${item.id}`
                  }
                </Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[1] }]}>
                <Text style={styles.tableCell}>{item.id || ''}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[2] }]}>
                <Text style={styles.tableCell}>
                  {item.property_date ? new Date(item.property_date).toLocaleDateString() : ''}
                </Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[3] }]}>
                <Text style={styles.tableCell}>
                  {item.delivery_first_name ? `${item.delivery_first_name} ${item.delivery_last_name || ''}`.trim() : ''}
                </Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[4] }]}>
                <Text style={styles.tableCellCenter}>{item.order_status || ''}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[5] }]}>
                <Text style={styles.tableCellRight}>
                  {item.totalRevenue ? `£${item.totalRevenue.toFixed(2)}` : 
                   item.order_total ? `£${(showPricesWithoutVAT ? 
                     parseFloat(item.order_total) - parseFloat(item.vat_amount || 0) : 
                     parseFloat(item.order_total)).toFixed(2)}` : ''}
                </Text>
              </View>
              {showPricesWithoutVAT && (
                <>
                  <View style={[styles.tableCol, { width: columnWidths[6] }]}>
                    <Text style={styles.tableCellRight}>
                      {item.totalVAT ? `£${item.totalVAT.toFixed(2)}` : 
                       item.vat_amount ? `£${parseFloat(item.vat_amount).toFixed(2)}` : ''}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: columnWidths[7] }]}>
                    <Text style={styles.tableCellRight}>
                      {item.totalRevenue ? `£${(item.totalRevenue + (item.totalVAT || 0)).toFixed(2)}` : 
                       item.order_total ? `£${parseFloat(item.order_total).toFixed(2)}` : ''}
                    </Text>
                  </View>
                </>
              )}
            </View>
          );
        })}

        {/* Grand Total row */}
        <View style={[styles.tableRow, styles.grandTotalRow]}>
          <View style={[styles.tableCol, { width: columnWidths[0] }]}>
            <Text style={styles.tableCellTotal}>GRAND TOTAL</Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[1] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[2] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[3] }]}>
            <Text style={styles.tableCellTotal}>
              {totalProperties} properties, {grandTotalOrders} orders
            </Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[4] }]}>
            <Text style={styles.tableCellTotal}></Text>
          </View>
          <View style={[styles.tableCol, { width: columnWidths[5] }]}>
            <Text style={styles.tableCellTotal}>£{grandTotalRevenue.toFixed(2)}</Text>
          </View>
          {showPricesWithoutVAT && (
            <>
              <View style={[styles.tableCol, { width: columnWidths[6] }]}>
                <Text style={styles.tableCellTotal}>£{grandTotalVAT.toFixed(2)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[7] }]}>
                <Text style={styles.tableCellTotal}>£{(grandTotalRevenue + grandTotalVAT).toFixed(2)}</Text>
              </View>
            </>
          )}
        </View>
      </View>
    </View>
  );
};

// Main PDF Document Component
const SalesReportPDF = ({ 
  reportData, 
  activeTab, 
  metrics, 
  dateRange, 
  selectedClient, 
  showPricesWithoutVAT,
  orderDetailsViewMode,
  preparePropertySummaryData,
  prepareOrderDetailsData
}) => {
  const dateRangeStr = `${dateRange[0]} to ${dateRange[1]}`;
  
  let title = 'Sales Report';
  if (activeTab === 'overview') {
    title = 'Sales Overview Report';
  } else if (activeTab === 'property') {
    title = 'Property Summary Report';
  } else if (activeTab === 'details') {
    if (orderDetailsViewMode === 'items') {
      title = 'Order Items Detail Report';
    } else if (orderDetailsViewMode === 'grouped') {
      title = 'Orders Grouped by Property';
    } else {
      title = 'Order Details Report';
    }
  }

  const clientText = selectedClient 
    ? `Client: ${selectedClient.name || `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim()}`
    : 'All Clients';

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page} wrap>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.headerTitle}>SALES REPORT SYSTEM</Text>
            <Text style={styles.headerSubtitle}>{title}</Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.headerInfo}>{clientText}</Text>
            <Text style={styles.headerInfo}>Period: {dateRangeStr}</Text>
          </View>
        </View>

        {/* Report Info */}
        <View style={styles.infoBox}>
          <Text style={styles.infoTitle}>Report Summary</Text>
          <Text style={styles.infoText}>
            Generated on: {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
          </Text>
          <Text style={styles.infoText}>
            Data period: {dateRangeStr} | Total records: {reportData?.length || 0}
          </Text>
          {showPricesWithoutVAT && (
            <Text style={styles.infoText}>
              Note: All amounts shown exclude VAT unless specified otherwise
            </Text>
          )}
        </View>

        {/* Content based on active tab */}
        <View style={{ flex: 1, paddingBottom: 40 }}>
          {activeTab === 'overview' && (
            <OverviewReport metrics={metrics} showPricesWithoutVAT={showPricesWithoutVAT} />
          )}

          {activeTab === 'property' && (
            <PropertySummaryTable 
              data={preparePropertySummaryData ? preparePropertySummaryData(reportData, { showPricesWithoutVAT, includeCanceled: true }) : []}
              showPricesWithoutVAT={showPricesWithoutVAT}
            />
          )}

          {activeTab === 'details' && orderDetailsViewMode === 'default' && (
            <OrderDetailsTable 
              data={reportData}
              showPricesWithoutVAT={showPricesWithoutVAT}
            />
          )}

          {activeTab === 'details' && orderDetailsViewMode === 'items' && (
            <OrderItemsDetail 
              data={reportData}
              showPricesWithoutVAT={showPricesWithoutVAT}
            />
          )}

          {activeTab === 'details' && orderDetailsViewMode === 'grouped' && (
            <GroupedOrdersTable 
              data={reportData}
              showPricesWithoutVAT={showPricesWithoutVAT}
              prepareOrderDetailsData={prepareOrderDetailsData}
            />
          )}
        </View>

        {/* Fixed Footer with page numbers */}
        <Text 
          style={styles.pageNumber} 
          render={({ pageNumber, totalPages }) => (
            `Page ${pageNumber} of ${totalPages}`
          )} 
          fixed 
        />
      </Page>
    </Document>
  );
};

export default SalesReportPDF;